import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Circle } from "lucide-react";

import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";

const radioGroupItemVariants = cva("flex flex-col gap-1 cursor-pointer", {
  variants: {
    variant: {
      default: undefined,
      radioBar: `
        p-4 border border-brand-neutral-black-4 rounded-lg
        has-[button[aria-checked='true']]:border-brand-primary-400
        has-[button[aria-checked='true']]:bg-[#F6F8F8]
      `,
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid gap-2", className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
    label?: React.ReactNode;
    description?: React.ReactNode;
  } & VariantProps<typeof radioGroupItemVariants>
>((props, ref) => {
  if (props.variant === "radioBar") {
    return <NewRadioGroupItem {...props} />;
  }

  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <RadioGroupPrimitive.Item
        ref={ref}
        className={cn(
          "aspect-square h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          props.className,
        )}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <Circle className="h-2.5 w-2.5 fill-current text-current" />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      {props.children && (
        <span className="text-sm font-medium text-[#0c0d0d]">
          {props.children}
        </span>
      )}
    </label>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

const NewRadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
    label?: React.ReactNode;
    description?: React.ReactNode;
  } & VariantProps<typeof radioGroupItemVariants>
>(({ className, variant, label, description, children, ...props }, ref) => {
  return (
    <label className={cn(radioGroupItemVariants({ variant }), className)}>
      <div className="flex items-center self-stretch gap-3">
        <RadioGroupPrimitive.Item
          ref={ref}
          className={cn(
            "aspect-square h-4 w-4 rounded-full border border-[#757575] data-[state=checked]:border-brand-primary-400 text-brand-primary-400",
            "ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:bg-[#D9D9D9] disabled:border-[#B3B3B3] disabled:cursor-not-allowed disabled:opacity-50",
          )}
          {...props}
        >
          <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
            <Circle className="h-2.5 w-2.5 fill-current text-current" />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
        {label && (
          <span className="text-base font-semibold text-brand-neutral-black-10">
            {label}
          </span>
        )}
      </div>
      {description && (
        <div className="flex items-center self-stretch gap-3">
          <div className="h-4 w-4" />
          <span className="text-base font-normal text-brand-neutral-black-10">
            {description}
          </span>
        </div>
      )}
      {children && (
        <span className="text-sm font-medium text-[#0c0d0d]">{children}</span>
      )}
    </label>
  );
});
NewRadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
